import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@/ui';

import type { ChangeEvent, FC } from 'react';

import {
  getCompanyMutationErrorMessage,
  getCompanyMutationFieldErrors,
  useCreateCompanyMutation,
} from '@/store/api/companyApi';
import { parseCompanyDomains } from '@/utils/companyDomains';
import { captureException } from '@/utils/errorMonitoring';

interface ModalProps {
  onClose: () => void;
}

const Modal: FC<ModalProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [domainsInput, setDomainsInput] = useState('');
  const [domainsError, setDomainsError] = useState<string>();
  const [generalError, setGeneralError] = useState<string>();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string>();
  const [createCompany, { isLoading }] = useCreateCompanyMutation();
  const createInFlightRef = useRef(false);

  const onDialogClose = () => {
    if (!createInFlightRef.current && !isLoading) onClose();
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError(undefined);
    setGeneralError(undefined);
  };
  const onDomainsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDomainsInput(e.target.value);
    setDomainsError(undefined);
    setGeneralError(undefined);
  };
  const onCreate = async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;

    setDomainsError(undefined);
    setGeneralError(undefined);
    setNameError(undefined);

    const parsedDomains = parseCompanyDomains(domainsInput);
    if (!parsedDomains.success) {
      createInFlightRef.current = false;
      setDomainsError(parsedDomains.error);
      return;
    }

    try {
      const company = await createCompany({
        domains: parsedDomains.domains,
        name: name.trim(),
      }).unwrap();

      navigate(`/coach/manage/companies/${company.id}`);
      onClose();
    } catch (e) {
      const fieldErrors = getCompanyMutationFieldErrors(e);
      if (fieldErrors.domains) {
        setDomainsError(fieldErrors.domains.join(','));
      }
      if (fieldErrors.name) setNameError(fieldErrors.name.join(','));
      if (!fieldErrors.domains && !fieldErrors.name) {
        setGeneralError(getCompanyMutationErrorMessage(e));
      }
      captureException(e);
    } finally {
      createInFlightRef.current = false;
    }
  };

  return (
    <Dialog
      aria-describedby="modal-description"
      aria-labelledby="modal-title"
      open
      onClose={onDialogClose}
    >
      <DialogTitle id="modal-title">New Company</DialogTitle>
      <DialogContent>
        <DialogContentText className="mb-4" id="modal-description">
          Create a new company.
        </DialogContentText>
        {generalError && <Alert severity="error">{generalError}</Alert>}
        <TextField
          disabled={isLoading}
          error={!!nameError}
          helperText={nameError}
          inputProps={{ 'aria-required': true }}
          label="Name"
          value={name}
          variant="outlined"
          onChange={onNameChange}
        />
        <TextField
          className="mt-4"
          disabled={isLoading}
          error={!!domainsError}
          helperText={domainsError}
          inputProps={{ 'aria-required': true }}
          label="Domains"
          value={domainsInput}
          variant="outlined"
          onChange={onDomainsChange}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          disabled={isLoading}
          variant="outlined"
          onClick={onDialogClose}
        >
          Cancel
        </Button>
        <Button
          color="secondary"
          disabled={
            !name.trim().length || !domainsInput.trim().length || isLoading
          }
          loading={isLoading}
          variant="contained"
          onClick={onCreate}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Modal;
